/* @flow */
import React from 'react';
import Text from 'components/Text';
import assertions from 'lib/assertions';
import EVENTS from 'integrations/events';
import Input from 'components/Input';
import Button from 'components/Button';
import EventTracking from 'lib/EventTracking';
import Textarea from 'components/Textarea';
import Columns2 from 'components/Columns2';
import Checkbox from 'components/Checkbox';
import ToastActions from 'actions/ToastActions';
import { asterisked } from 'components/Label';
import FormElement from 'components/FormElement';
import AdminNoticeBanner from 'admin/components/AdminNoticeBanner';
import ContentSourceProviderContainer from '../containers/ContentSourceProviderContainer';
import ContentSourceProviderProfilePictureInput from './ContentSourceProviderProfilePictureInput';
import Box from 'components/Box';
import BoxBody from 'components/BoxBody';
import BoxFooter from 'components/BoxFooter';
import BoxHeader from 'components/BoxHeader';

import './styles/ContentSourceProviderForm.scss';

type Props = {
  uuid: string,
  onSave?: Function,
};

type State = {
  title?: ?string,
  providerIconFileUUID?: ?string,
  contentWebhookUrl?: ?string,
  enabledByProvider?: ?boolean,
  bigPreviewWebhookUrl?: ?string,
  productLongDescription?: ?string,
  productShortDescription?: ?string,
};

const INVALID = 'INVALID';

class ContentSourceProviderForm extends React.Component<Props, State> {
  state: State = {};

  componentDidMount() {
    EventTracking.trackKey(EVENTS.CONTENT_SOURCE_FORM.VIEW, {
      organization_uuid: this.props.uuid,
    });
  }

  isSubmittable = () => {
    return Object.values(this.state).every(e => e !== INVALID);
  };

  submit(update: Function) {
    EventTracking.trackKey(EVENTS.CONTENT_SOURCE_FORM.SUBMIT, {
      organization_uuid: this.props.uuid,
    });

    update(this.state)
      .then(() => {
        ToastActions.showSuccessToast('Saved!');
        if (this.props.onSave) {
          this.props.onSave();
        }
      })
      .catch(e => {
        console.debug(e);
        console.error('ContentSourceProviderForm - could not submit');
        ToastActions.showErrorToast(e.toString());
      });
  }

  render() {
    return (
      <ContentSourceProviderContainer uuid={this.props.uuid}>
        {({ contentSourceProvider, update, loading }) => {
          if (loading) {
            return null;
          }

          return (
            <Box>
              <BoxHeader title="Content Source Form" />
              <BoxBody>
                <div>
                  <Columns2>
                    <FormElement
                      element={Input}
                      disabled={loading}
                      onValidChange={e => this.setState({ title: e.target.value })}
                      onInvalidChange={() => this.setState({ title: INVALID })}
                      label={asterisked('Display name')}
                      validate={e => {
                        assertions.notEmpty(e.target.value);
                        assertions.lengthLesserThanOrEqualTo(e.target.value, 25);
                      }}
                      elementProps={{
                        disabled: loading,
                        placeholder: 'Acme Inc',
                        defaultValue: contentSourceProvider && contentSourceProvider.title,
                      }}
                    />
                    <div>
                      <FormElement
                        disabled={loading}
                        showErrorBeforeBlur
                        label={asterisked('Display icon')}
                        element={ContentSourceProviderProfilePictureInput}
                        onValidChange={e => {
                          // we get backed the signed URL, but we need the UUID that's in the middle of the path URL.
                          try {
                            const url = new URL(e.target.value);
                            const regexResult = (url.pathname.match(/.*\/images\/(.*)\//) || [
                              undefined,
                              undefined,
                            ])[1];
                            this.setState({ providerIconFileUUID: regexResult });
                          } catch (err) {
                            console.error(err);
                            ToastActions.showErrorToast('something went wrong uploading image');
                          }
                        }}
                        onInvalidChange={() => this.setState({ providerIconFileUUID: INVALID })}
                        validate={e => {
                          assertions.notEmpty(e.target.value);
                        }}
                        elementProps={{
                          disabled: loading,
                          placeholder: 'Click to upload file',
                          defaultValue:
                            contentSourceProvider && contentSourceProvider.providerIconFileUUID,
                        }}
                      />
                    </div>
                  </Columns2>

                  <FormElement
                    element={Input}
                    disabled={loading}
                    onValidChange={e => this.setState({ productShortDescription: e.target.value })}
                    onInvalidChange={() => this.setState({ productShortDescription: INVALID })}
                    label={asterisked('Short product description')}
                    validate={e => {
                      assertions.lengthGreaterThanOrEqualTo(e.target.value, 50);
                      assertions.lengthLesserThanOrEqualTo(e.target.value, 80);
                      assertions.notEmpty(e.target.value);
                    }}
                    elementProps={{
                      disabled: loading,
                      defaultValue:
                        contentSourceProvider && contentSourceProvider.productShortDescription,
                      placeholder: 'Acme Inc. offers a wide range of products to coyotes',
                    }}
                  />

                  <FormElement
                    element={Textarea}
                    disabled={loading}
                    onValidChange={e => this.setState({ productLongDescription: e.target.value })}
                    onInvalidChange={() => this.setState({ productLongDescription: INVALID })}
                    label={asterisked('Long product description')}
                    validate={e => {
                      assertions.lengthGreaterThanOrEqualTo(e.target.value, 50);
                      assertions.lengthLesserThanOrEqualTo(e.target.value, 200);
                      assertions.notEmpty(e.target.value);
                    }}
                    elementProps={{
                      uncontrolled: true,
                      disabled: loading,
                      defaultValue:
                        contentSourceProvider && contentSourceProvider.productLongDescription,
                      placeholder:
                        'The Acme Corporation is a corporation that offers top of the line products that never fail or backfire catastrophically at the worst possible times.',
                    }}
                  />

                  <FormElement
                    element={Input}
                    disabled={loading}
                    onValidChange={e => this.setState({ contentWebhookUrl: e.target.value })}
                    onInvalidChange={() => this.setState({ contentWebhookUrl: INVALID })}
                    label={asterisked('Content webhook url')}
                    validate={e => {
                      assertions.notEmpty(e.target.value);
                      assertions.url(e.target.value);
                    }}
                    elementProps={{
                      disabled: loading,
                      defaultValue:
                        contentSourceProvider && contentSourceProvider.contentWebhookUrl,
                      placeholder: 'https://api.acmeinc.com/webhooks/remind/content',
                    }}
                  />
                  <FormElement
                    element={Input}
                    disabled={loading}
                    showErrorBeforeBlur
                    onValidChange={e => this.setState({ bigPreviewWebhookUrl: e.target.value })}
                    onInvalidChange={() => this.setState({ bigPreviewWebhookUrl: INVALID })}
                    label="Content preview webhook url"
                    validate={e => {
                      if (e.target.value.length > 0) {
                        assertions.url(e.target.value);
                      }
                    }}
                    elementProps={{
                      disabled: loading,
                      defaultValue:
                        contentSourceProvider && contentSourceProvider.bigPreviewWebhookUrl,
                      placeholder: 'https://api.acmeinc.com/webhooks/remind/content_preview',
                    }}
                  />

                  <FormElement
                    labelHidden
                    element={Checkbox}
                    disabled={loading}
                    showErrorBeforeBlur
                    onValidChange={e => this.setState({ enabledByProvider: e.target.checked })}
                    onInvalidChange={() => this.setState({ enabledByProvider: undefined })}
                    label="Enable integration"
                    elementProps={{
                      disabled: loading,
                      label: 'Enable integration',
                      defaultChecked:
                        contentSourceProvider && contentSourceProvider.enabledByProvider,
                    }}
                  />

                  {contentSourceProvider && (
                    <div className="ContentSourceProviderForm-notice">
                      {contentSourceProvider.title &&
                        contentSourceProvider.enabledByProvider &&
                        !contentSourceProvider.enabledByRemind && (
                          <AdminNoticeBanner
                            color="sunrise"
                            actionElement={null}
                            glyph="success-checkmark-stroke">
                            <Text element="div">
                              This integration has not been enabled by Remind.
                            </Text>
                          </AdminNoticeBanner>
                        )}

                      {contentSourceProvider.enabledByRemind &&
                        contentSourceProvider.enabledByProvider && (
                          <AdminNoticeBanner
                            color="brand"
                            actionElement={null}
                            glyph="success-checkmark-stroke">
                            <Text element="div">This integration is live!</Text>
                          </AdminNoticeBanner>
                        )}
                    </div>
                  )}
                </div>
              </BoxBody>
              <BoxFooter>
                <Button
                  onClick={() => this.submit(update)}
                  disabled={loading || !this.isSubmittable()}>
                  Save
                </Button>
              </BoxFooter>
            </Box>
          );
        }}
      </ContentSourceProviderContainer>
    );
  }
}

export default ContentSourceProviderForm;
